import { htmlEscape, assertNoUnsubstitutedTokens, appendUtmsToTrovexLinks } from './_shared.js';
import { sanitiseEmailHtml } from './_sanitise.js';

// Plain-text template renders WITHOUT any branded chrome — no logo bar,
// no accent rule, no rounded container, no company-name line, no
// compliance footer block. It should read like a 1:1 email someone
// composed in Outlook: default font (Calibri / Aptos / Segoe UI),
// rich-text body and signature controlled entirely by the author, single
// small unsubscribe line at the bottom for GDPR. The result loses the
// marketing polish of the other templates by design — that's the point.

function preheaderSpan(text) {
  if (!text) return '';
  const safe = htmlEscape(text).slice(0, 140);
  return `<div style="display: none; max-height: 0; overflow: hidden; opacity: 0; mso-hide: all; font-size: 1px; line-height: 1px; color: transparent;">${safe}&zwnj;${'&nbsp;'.repeat(60)}</div>`;
}

function rich(html) {
  if (!html) return '';
  // Sanitise (drops scripts / unsafe URLs / on*= handlers). The output
  // is trusted enough to embed; we wrap in a styled container so the
  // intrinsic font/colour matches the rest of the letter.
  return `<div style="margin: 0 0 14px;">${sanitiseEmailHtml(html)}</div>`;
}

export default {
  id: 'plainText',
  name: 'Plain text (Outlook-style)',
  description: 'No logo, no branded frame. Reads like a 1:1 Outlook email, with teal buttons for files and event cards kept. High open rate, personal feel.',
  slots: [
    { id: 'preheader', label: 'Preview text', type: 'text',     optional: true,  maxLen: 140 },
    { id: 'greeting',  label: 'Greeting',     type: 'text',     optional: false, maxLen: 80,
      hint: 'Use {{firstName}} for personalisation, e.g. "Hi {{firstName}},"' },
    { id: 'body',      label: 'Body',         type: 'richtext', optional: false,
      hint: 'Bold, italic, links, lists — formatting is preserved.' },
    { id: 'signature', label: 'Signature',    type: 'richtext', optional: false,
      hint: 'Multiline allowed. Include role, phone, an inline image, etc.' },
  ],
  render(content, branding, footer, options) {
    const c = content || {};
    // Unsubscribe is Postmark-managed: the `{{{ pm:unsubscribe }}}` token is
    // substituted server-side at send (broadcast stream). Emitting it here means
    // exactly ONE real unsubscribe link — previously the template emitted its
    // own link AND Postmark auto-appended a second one (the "double Unsubscribe"
    // in Outlook). `options.unsubscribeUrl` is ignored now. In the composer
    // preview the token shows as literal text in the href (harmless; the visible
    // "Unsubscribe" label is correct).

    const html = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <meta name="x-apple-disable-message-reformatting">
    <meta name="color-scheme" content="light only">
    <meta name="supported-color-schemes" content="light">
    <title></title>
    <!--[if mso]>
      <noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript>
    <![endif]-->
  </head>
  <body style="margin: 0; padding: 24px; background-color: #ffffff; font-family: Calibri, Aptos, 'Segoe UI', Tahoma, Verdana, sans-serif; font-size: 15px; line-height: 1.55; color: #1f1f1f;">
    ${preheaderSpan(c.preheader || c.greeting)}
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width: 100%;">
      <tr>
        <td style="padding: 0;">
          <p style="margin: 0 0 14px;">${htmlEscape(c.greeting || '')}</p>
          ${rich(c.body)}
          <div style="margin-top: 18px;">${rich(c.signature) || ''}</div>
          <p style="margin: 28px 0 0; font-size: 11px; color: #888;">
            <a href="{{{ pm:unsubscribe }}}" style="color: #888;">Unsubscribe</a>
          </p>
        </td>
      </tr>
    </table>
  </body>
</html>`;
    assertNoUnsubstitutedTokens(html);
    return appendUtmsToTrovexLinks(html, options?.utm);
  },
};
